import { Typography } from "@material-ui/core";
import { AccessRoleAccessRightDto } from "Api/Api";
import {
  CheckboxCheckedIcon,
  CheckboxUncheckedIcon,
} from "Components/Shared/Icons";
import { SearchBar } from "Components/Shared/SearchBar";
import { StyledFlex } from "Components/Shared/StyledComponents";
import latinize from "latinize";
import * as React from "react";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { setDisableRedirect } from "State/App/AppReducer";
import { useDispatch } from "react-redux";

type Props = {
  accessRights: AccessRoleAccessRightDto[];
  setAccessRights: (val: AccessRoleAccessRightDto[]) => void;
};

const StyledWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing(5)}px;
  width: 100%;
  & > p {
    font-weight: 300;
    color: ${props => props.theme.palette.secondary.main};
    margin-bottom: ${props => props.theme.spacing(2)}px;
    margin-top: ${props => props.theme.spacing(3)}px;
  }
`;

const StyledRole = styled.div`
  cursor: pointer;
`;

const StyledRightWrapper = styled.div`
  display: grid;
  grid-template-columns: 25px 300px 1fr;
  align-items: center;
  width: 100%;
  max-width: 650px;
  padding: ${props => props.theme.spacing(1.25, 0)};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  gap: ${props => props.theme.spacing(0, 1)};

  p {
    text-align: left;
    word-break: break-word;
    &:last-of-type {
      font-weight: 300;
      font-size: 12px;
      color: ${props => props.theme.palette.secondary.main};
    }
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr auto;
    p {
      &:last-of-type {
        grid-area: 2/1;
      }
    }
    & > div {
      grid-area: 1/2/3/2;
    }
  }
`;

const StyledRightsWrapper = styled.div`
  overflow: auto;
  max-height: calc(${props => props.theme.vh(100)} - 410px);
  min-height: 400px;
`;

const StyledSearchBar = styled.div`
  width: 350px;

  ${props => props.theme.breakpoints.down("xs")} {
    width: 100%;
  }

  margin-bottom: ${props => props.theme.spacing(3)}px;
`;

const textSearch = (
  includes: (text: string) => boolean,
  e: AccessRoleAccessRightDto,
): unknown => {
  return includes(e.accessRightCode) || (e.note && includes(e.note));
};

export const AccessRoleAccessRights: React.FunctionComponent<Props> = props => {
  const { t } = useResource();
  const dispatch = useDispatch();
  const { accessRights, setAccessRights } = props;
  const [searchQuery, setSearchQuery] = React.useState("");

  const search = (right: AccessRoleAccessRightDto) => {
    const includes = (text: string | undefined) =>
      !!text && latinize(text.toLocaleLowerCase()).includes(searchQuery);

    return searchQuery.length > 0 ? textSearch(includes, right) : true;
  };

  const handleClick = (right: AccessRoleAccessRightDto) => {
    const updatedRoles = accessRights.map(e => {
      if (e.accessRightCode === right.accessRightCode) {
        return { ...e, isSelected: !right.isSelected };
      }
      return e;
    });
    setAccessRights(updatedRoles);
    dispatch(setDisableRedirect(true));
  };

  return (
    <StyledWrapper>
      <StyledSearchBar>
        <SearchBar
          placeholder={t(Resources.AccessRoles.Detail.SearchBar)}
          onSearch={q => {
            setSearchQuery(latinize(q?.toLocaleLowerCase()) || "");
          }}
          debounceTime={100}
        />
      </StyledSearchBar>

      <div className={"form-title"}>
        {t(Resources.AccessRoles.Detail.AccessRights)}
      </div>

      <StyledRightsWrapper>
        {accessRights
          ?.filter(x => search(x))
          ?.map(right => (
            <StyledRole
              key={right.accessRightCode}
              onClick={() => handleClick(right)}
            >
              <StyledRightWrapper>
                <StyledFlex>
                  {right.isSelected ? (
                    <CheckboxCheckedIcon />
                  ) : (
                    <CheckboxUncheckedIcon />
                  )}
                </StyledFlex>
                <Typography>{right.accessRightCode}</Typography>
                <Typography>{right.note}</Typography>
              </StyledRightWrapper>
            </StyledRole>
          ))}
      </StyledRightsWrapper>
    </StyledWrapper>
  );
};
